// ─── Core CRM Types (from crm.ts) ────────────────────────────────────────────
export type CustomerContact = {
  name: string; position: string; phone: string;
  role: 'decision' | 'influence' | 'user' | 'finance';
  wechat?: string; note?: string;
};

export type CustomerBehavior = {
  date: string; action: string; detail: string;
  channel: 'material' | 'website' | 'event' | 'wechat' | 'email';
};

// 分享追踪：单次打开记录
export type ShareOpenRecord = {
  id: number;
  openTime: string;       // 打开时间
  user: string;           // 打开用户（姓名/昵称）
  device: string;         // 设备类型
  os: string;             // 操作系统
  location: string;       // 打开地址（城市）
  ip: string;             // IP（脱敏）
  duration: number;       // 停留时长（秒）
  pageViews: number;      // 浏览页数
  hotPage: string;        // 停留最久的页面
  hotPageDuration: number; // 该页停留时长（秒）
  isForward: boolean;     // 是否是转发打开
  forwardFrom?: string;   // 转发来源
};

// 分享追踪：单条分享记录
export type MaterialShareRecord = {
  id: number;
  materialName: string;   // 物料名称
  materialType: string;   // 物料类型
  shareTime: string;      // 分享时间
  sharedBy: string;       // 分享人（销售）
  shareLink: string;      // 分享链接（脱敏）
  totalOpens: number;     // 总打开次数
  totalForwards: number;  // 转发次数
  uniqueUsers: number;    // 独立访客数
  avgDuration: number;    // 平均停留时长（秒）
  openRecords: ShareOpenRecord[];
};

// 每周互动热力图数据（最近12周）
export type WeeklyHeatmapData = {
  week: string;           // 周标识，如 "W1"
  weekLabel: string;      // 显示标签，如 "3/4"
  days: number[];         // 7天互动强度 0-4（0无 1低 2中 3高 4极高）
  total: number;          // 本周总互动次数
};

// 每日互动明细记录
export type DailyInteractionRecord = {
  id: number;
  time: string;           // 时间，如 "10:32"
  type: 'material' | 'website' | 'wechat' | 'email' | 'call' | 'event';
  title: string;          // 互动标题
  detail: string;         // 详细描述
  duration?: number;      // 停留/通话时长（秒）
};

// 每日互动明细（按客户ID + 日期索引）
export type DailyInteractionMap = Record<number, Record<string, DailyInteractionRecord[]>>;

export type CrmCustomer = {
  id: number; name: string; company: string; position: string;
  phone: string; email: string; avatar: string;
  status: 'active' | 'follow' | 'close' | 'lost';
  source: string; tags: string[]; lastContact: string;
  totalValue: number; assignee: string;
  // 企业工商信息
  creditCode?: string;       // 统一社会信用代码
  registeredCapital?: string; // 注册资本
  foundedYear?: number;       // 成立年份
  industry?: string;          // 所属行业
  companySize?: string;       // 企业规模（人数）
  businessScope?: string;     // 主营业务
  companyAddress?: string;    // 注册地址
  // 客户画像
  annualBudget?: string;      // 年度预算区间
  purchaseCycle?: string;     // 采购周期
  decisionPeriod?: string;    // 决策周期
  painPoints?: string[];      // 核心痛点
  // 决策链
  contacts?: CustomerContact[];
  // 竞争情报
  currentVendors?: string[];  // 当前使用竞品/供应商
  switchIntent?: 'high' | 'medium' | 'low' | 'none'; // 替换意向
  competitorNotes?: string;   // 竞品备注
  // 行为追踪
  behaviors?: CustomerBehavior[];
  materialOpenCount?: number; // 物料打开次数
  websiteVisits?: number;     // 官网访问次数
  eventAttended?: string[];   // 参加过的活动
  // 风险评估
  creditRating?: 'A' | 'B' | 'C' | 'D'; // 信用评级
  paymentHabit?: 'excellent' | 'good' | 'normal' | 'poor'; // 付款习惯
  churnRisk?: 'low' | 'medium' | 'high'; // 流失风险
  churnReason?: string;       // 流失/风险原因
  // 备注
  internalNote?: string;      // 内部备注（不对外展示）
};

export type FollowRecord = {
  id: number; customerId: number; customerName: string;
  type: 'call' | 'visit' | 'email' | 'wechat' | 'meeting';
  content: string; result: string; nextAction: string;
  createdAt: string; assignee: string;
};

export type Opportunity = {
  id: number; title: string; customerName: string;
  value: number; probability: number;
  stage: 'lead' | 'contact' | 'demo' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  expectedClose: string; assignee: string; tags: string[];
};

export type SalesLead = {
  id: number; companyName: string; contactName: string; phone: string;
  source: string; score: number;
  status: 'new' | 'contacted' | 'qualified' | 'unqualified';
  industry: string; createdAt: string; assignee: string;
};

// ─── Extended CRM Types (from crmExtended.ts) ────────────────────────────────
export type Contract = {
  id: number; contractNo: string; title: string;
  customerId: number; customerName: string; amount: number;
  status: 'draft' | 'pending' | 'active' | 'expired' | 'terminated';
  signDate: string; startDate: string; endDate: string;
  assignee: string; notes: string;
};

export type CrmPayment = {
  id: number; contractNo: string; customerName: string;
  amount: number; dueDate: string; paidDate: string | null;
  status: 'pending' | 'paid' | 'overdue';
  invoiceStatus: 'none' | 'applied' | 'issued';
};

export type CrmProduct = {
  id: number; name: string; category: string; price: number;
  unit: string; stock: number | null; description: string;
  status: 'active' | 'inactive';
  image?: string;
  images?: string[];
  sku?: string;
  tags?: string[];
  highlights?: string[];
  stockAlert?: number | null;  // 低库存预警阈值，null 表示不启用
};

export type ProductCategory = {
  id: string;
  name: string;
  icon: string;
  color: string;
  bg: string;
};

export type FollowTask = {
  id: number; customerId: number; customerName: string;
  customerAvatar: string;
  taskType: 'call' | 'visit' | 'email' | 'wechat' | 'meeting';
  plannedAt: string; status: 'pending' | 'done' | 'overdue';
  priority: 'high' | 'medium' | 'low';
  note: string; assignee: string;
};

export type CrmActivity = {
  id: number;
  type: 'new_lead' | 'follow' | 'deal' | 'contract' | 'payment' | 'lost';
  content: string; time: string; user: string;
};

export type CrmTag = {
  id: number; name: string; color: string;
  category: 'system' | 'custom'; count: number;
};

// ─── Core Data ────────────────────────────────────────────────────────────────
export const crmCustomers: CrmCustomer[] = [
  {
    id: 1, name: '陈志远', company: '深圳华锐科技有限公司', position: '总经理',
    phone: '138****8801', email: 'chen@huarui.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20business%20executive%20portrait%20headshot%2C%20formal%20attire%2C%20confident%20smile%2C%20clean%20white%20background%2C%20corporate%20photography%20style%2C%20high%20quality&width=100&height=100&seq=crm-c1&orientation=squarish',
    status: 'active', source: '转介绍', tags: ['高价值', 'KA客户', '续约中'], lastContact: '2026-03-18', totalValue: 280000, assignee: '李明',
    creditCode: '91440300MA5FXXXX8Q', registeredCapital: '5000万元', foundedYear: 2015, industry: '人工智能/软件服务',
    companySize: '200-500人', businessScope: 'AI技术研发、企业数字化解决方案、软件系统集成',
    companyAddress: '深圳市南山区科技园南区A栋18层',
    annualBudget: '50-100万', purchaseCycle: '年度采购', decisionPeriod: '2-4周',
    painPoints: ['销售团队效率低', '客服成本高', '线索转化率不足'],
    contacts: [
      { name: '陈志远', position: '总经理', phone: '138****8801', role: 'decision', wechat: 'chen_zr', note: '最终拍板人，关注ROI' },
      { name: '李副总', position: '副总经理', phone: '139****1122', role: 'influence', note: '负责技术评估' },
      { name: '赵采购', position: '采购经理', phone: '136****3344', role: 'finance', note: '负责合同及付款' },
    ],
    currentVendors: ['销售易CRM', '企业微信SCRM'], switchIntent: 'medium',
    competitorNotes: '目前使用销售易，对AI能力不满意，有替换意向',
    behaviors: [
      { date: '2026-03-18', action: '查看物料', detail: '打开《AI员工ROI分析报告》，停留12分钟', channel: 'material' },
      { date: '2026-03-15', action: '访问官网', detail: '浏览产品定价页面3次，查看案例页', channel: 'website' },
      { date: '2026-03-10', action: '参加活动', detail: '出席「2026企业AI峰会」，与销售深度交流', channel: 'event' },
    ],
    materialOpenCount: 8, websiteVisits: 15, eventAttended: ['2026企业AI峰会', '深圳数字化转型论坛'],
    creditRating: 'A', paymentHabit: 'excellent', churnRisk: 'low',
    internalNote: '陈总对价格敏感度低，更关注服务质量和续约保障，建议Q2前完成续约谈判',
  },
  {
    id: 2, name: '张美琪', company: '上海创锐广告传媒', position: '市场总监',
    phone: '139****2234', email: 'zhang@chuangrui.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20business%20executive%20portrait%20headshot%2C%20formal%20attire%2C%20warm%20smile%2C%20clean%20white%20background%2C%20corporate%20photography%20style%2C%20high%20quality&width=100&height=100&seq=crm-c2&orientation=squarish',
    status: 'follow', source: '展会获客', tags: ['新客户', '广告行业'], lastContact: '2026-03-16', totalValue: 65000, assignee: '王芳',
    creditCode: '91310000MA1GXXXX5T', registeredCapital: '1000万元', foundedYear: 2018, industry: '广告传媒/营销服务',
    companySize: '50-200人', businessScope: '品牌策划、数字营销、内容创作、社媒运营',
    companyAddress: '上海市静安区南京西路1788号',
    annualBudget: '20-50万', purchaseCycle: '半年度', decisionPeriod: '4-6周',
    painPoints: ['内容生产效率低', '多平台运营人力不足', '素材管理混乱'],
    contacts: [
      { name: '张美琪', position: '市场总监', phone: '139****2234', role: 'decision', wechat: 'zhang_mq', note: '主要决策人，关注内容效率' },
      { name: '王总', position: '总经理', phone: '138****5566', role: 'influence', note: '最终审批，需要ROI数据' },
    ],
    currentVendors: ['蓝色光标', '传统人工团队'], switchIntent: 'high',
    competitorNotes: '正在评估我们和另一家AI内容工具，价格是关键因素',
    behaviors: [
      { date: '2026-03-16', action: '接收物料', detail: '收到产品演示录屏，转发给内部3人', channel: 'wechat' },
      { date: '2026-03-12', action: '访问官网', detail: '查看物料库功能页，下载了功能手册', channel: 'website' },
    ],
    materialOpenCount: 3, websiteVisits: 7, eventAttended: ['2026上海广告展'],
    creditRating: 'B', paymentHabit: 'good', churnRisk: 'medium',
    internalNote: '决策周期较长，需要持续输出价值内容，建议安排一次线下演示',
  },
  {
    id: 3, name: '刘建国', company: '北京云合信息技术', position: 'CEO',
    phone: '137****5566', email: 'liu@yunhe.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20CEO%20portrait%20headshot%2C%20business%20casual%2C%20confident%20look%2C%20clean%20white%20background%2C%20corporate%20headshot%20style%2C%20high%20quality&width=100&height=100&seq=crm-c3&orientation=squarish',
    status: 'active', source: '老客户引荐', tags: ['战略客户', '技术行业'], lastContact: '2026-03-17', totalValue: 520000, assignee: '李明',
    creditCode: '91110000MA00XXXX3K', registeredCapital: '2亿元', foundedYear: 2012, industry: '云计算/企业服务',
    companySize: '500-2000人', businessScope: '云计算平台、企业数字化转型、SaaS产品研发',
    companyAddress: '北京市海淀区中关村软件园二期',
    annualBudget: '100-500万', purchaseCycle: '年度战略采购', decisionPeriod: '6-8周',
    painPoints: ['大规模客服成本高', '销售团队管理复杂', '数据分析能力不足'],
    contacts: [
      { name: '刘建国', position: 'CEO', phone: '137****5566', role: 'decision', wechat: 'liu_jg_ceo', note: '战略层决策，关注长期合作价值' },
      { name: '张CTO', position: '技术总监', phone: '136****7788', role: 'influence', note: '技术评估负责人，关注API集成能力' },
      { name: '孙CFO', position: '财务总监', phone: '135****9900', role: 'finance', note: '预算审批，要求分期付款' },
      { name: '李运营', position: '运营总监', phone: '134****1122', role: 'user', note: '日常使用负责人' },
    ],
    currentVendors: ['Salesforce', '钉钉'], switchIntent: 'low',
    competitorNotes: '已使用Salesforce多年，此次是增量采购AI能力，非替换',
    behaviors: [
      { date: '2026-03-17', action: '电话沟通', detail: '确认合同条款，对服务SLA要求99.9%', channel: 'wechat' },
      { date: '2026-03-10', action: '参加活动', detail: '参加我司举办的「AI员工私享会」，现场签意向书', channel: 'event' },
      { date: '2026-03-05', action: '查看物料', detail: '打开《企业级AI员工部署方案》，分享给技术团队', channel: 'material' },
    ],
    materialOpenCount: 12, websiteVisits: 22, eventAttended: ['AI员工私享会', '2026中关村论坛'],
    creditRating: 'A', paymentHabit: 'excellent', churnRisk: 'low',
    internalNote: '战略级客户，需要CEO级别维护，建议每季度高层拜访一次',
  },
  {
    id: 4, name: '王晓燕', company: '广州美橙电商运营', position: '运营总监',
    phone: '136****7788', email: 'wang@meicheng.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20operations%20director%20headshot%2C%20business%20attire%2C%20friendly%20expression%2C%20white%20background%2C%20corporate%20photo&width=100&height=100&seq=crm-c4&orientation=squarish',
    status: 'close', source: '网络推广', tags: ['电商', '已签约'], lastContact: '2026-03-10', totalValue: 98000, assignee: '赵磊',
    creditCode: '91440101MA9GXXXX7P', registeredCapital: '500万元', foundedYear: 2019, industry: '电商运营/跨境贸易',
    companySize: '20-50人', businessScope: '电商代运营、品牌孵化、直播电商、跨境出口',
    companyAddress: '广州市天河区珠江新城华夏路',
    annualBudget: '10-30万', purchaseCycle: '年度', decisionPeriod: '1-2周',
    painPoints: ['直播内容产出慢', '客服响应不及时', '素材复用率低'],
    contacts: [
      { name: '王晓燕', position: '运营总监', phone: '136****7788', role: 'decision', wechat: 'wang_xy_ops', note: '直接决策人，执行力强' },
    ],
    currentVendors: ['有赞', '快手小店'], switchIntent: 'none',
    competitorNotes: '已成交，无竞品威胁，关注续约',
    behaviors: [
      { date: '2026-03-10', action: '登录系统', detail: '使用物料库功能，上传素材32个', channel: 'website' },
      { date: '2026-03-03', action: '回款到账', detail: '全额付款98000元，付款及时', channel: 'wechat' },
    ],
    materialOpenCount: 5, websiteVisits: 18, eventAttended: [],
    creditRating: 'A', paymentHabit: 'excellent', churnRisk: 'low',
    internalNote: '合同即将到期，需提前30天启动续约谈判，可考虑升级套餐',
  },
  {
    id: 5, name: '孙鹏飞', company: '成都锐捷科技', position: '销售总监',
    phone: '135****4412', email: 'sun@ruijie.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20sales%20director%20headshot%2C%20suit%2C%20confident%20posture%2C%20white%20background%2C%20corporate%20portrait%20photography&width=100&height=100&seq=crm-c5&orientation=squarish',
    status: 'follow', source: '百度推广', tags: ['科技行业', '评估中'], lastContact: '2026-03-15', totalValue: 42000, assignee: '王芳',
    creditCode: '91510100MA62XXXX1W', registeredCapital: '800万元', foundedYear: 2017, industry: '软件开发/IT服务',
    companySize: '50-200人', businessScope: '企业软件开发、IT外包服务、系统集成',
    companyAddress: '成都市高新区天府大道北段',
    annualBudget: '10-20万', purchaseCycle: '半年度', decisionPeriod: '3-5周',
    painPoints: ['销售线索质量差', '跟进效率低', '报价流程繁琐'],
    contacts: [
      { name: '孙鹏飞', position: '销售总监', phone: '135****4412', role: 'influence', wechat: 'sun_pf', note: '推动者，需要向老板汇报' },
      { name: '张总', position: '总经理', phone: '138****8899', role: 'decision', note: '最终决策，较保守' },
    ],
    currentVendors: ['纷享销客', '腾讯企点'], switchIntent: 'medium',
    competitorNotes: '正在对比我们和纷享销客的AI功能，价格差距不大',
    behaviors: [
      { date: '2026-03-15', action: '接收邮件', detail: '打开竞品对比分析报告，阅读时长8分钟', channel: 'email' },
      { date: '2026-03-08', action: '访问官网', detail: '查看定价页面，对比基础版和专业版', channel: 'website' },
    ],
    materialOpenCount: 2, websiteVisits: 5, eventAttended: [],
    creditRating: 'B', paymentHabit: 'good', churnRisk: 'medium',
    internalNote: '决策人较保守，建议提供免费试用期降低决策门槛',
  },
  {
    id: 6, name: '林雪梅', company: '杭州织梦服饰', position: '品牌总监',
    phone: '133****9921', email: 'lin@zhimeng.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20brand%20director%20headshot%2C%20stylish%20business%20attire%2C%20elegant%20look%2C%20white%20studio%20background%2C%20high%20quality%20portrait&width=100&height=100&seq=crm-c6&orientation=squarish',
    status: 'active', source: '转介绍', tags: ['时尚行业', '高频次'], lastContact: '2026-03-19', totalValue: 175000, assignee: '赵磊',
    creditCode: '91330100MA27XXXX6H', registeredCapital: '3000万元', foundedYear: 2014, industry: '服装/时尚零售',
    companySize: '200-500人', businessScope: '女装设计研发、品牌运营、线上线下零售',
    companyAddress: '杭州市余杭区良渚文化村',
    annualBudget: '30-80万', purchaseCycle: '年度', decisionPeriod: '2-3周',
    painPoints: ['品牌内容产出量不足', '季节性营销节奏紧', '多渠道素材管理难'],
    contacts: [
      { name: '林雪梅', position: '品牌总监', phone: '133****9921', role: 'decision', wechat: 'lin_xm_brand', note: '主要决策人，审美要求高' },
      { name: '陈设计', position: '创意总监', phone: '132****4455', role: 'user', note: '日常使用，关注设计功能' },
    ],
    currentVendors: ['Adobe Creative Cloud', '蓝湖'], switchIntent: 'low',
    competitorNotes: '已成交，使用满意，正在评估升级高级版',
    behaviors: [
      { date: '2026-03-19', action: '上门拜访', detail: '现场演示物料库新功能，确认升级意向', channel: 'event' },
      { date: '2026-03-12', action: '查看物料', detail: '打开《物料库高级版功能对比》，转发给设计团队', channel: 'material' },
      { date: '2026-03-05', action: '访问官网', detail: '查看高级版定价，停留15分钟', channel: 'website' },
    ],
    materialOpenCount: 9, websiteVisits: 20, eventAttended: ['2026杭州时尚峰会'],
    creditRating: 'A', paymentHabit: 'good', churnRisk: 'low',
    internalNote: '合同已到期，续约谈判中，建议捆绑销售高级版+实施服务',
  },
  {
    id: 7, name: '周大伟', company: '武汉智链供应链', position: '副总裁',
    phone: '132****3355', email: 'zhou@zhilian.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20VP%20portrait%20headshot%2C%20formal%20business%20suit%2C%20authoritative%20look%2C%20white%20background%2C%20executive%20portrait&width=100&height=100&seq=crm-c7&orientation=squarish',
    status: 'lost', source: '电话开发', tags: ['供应链', '未成交'], lastContact: '2026-02-28', totalValue: 0, assignee: '李明',
    creditCode: '91420100MA4KXXXX2N', registeredCapital: '1500万元', foundedYear: 2016, industry: '供应链管理/物流',
    companySize: '100-500人', businessScope: '供应链金融、仓储物流、采购管理',
    companyAddress: '武汉市东湖高新区光谷大道',
    annualBudget: '20-50万', purchaseCycle: '年度', decisionPeriod: '8-12周',
    painPoints: ['供应商管理效率低', '采购流程不透明'],
    contacts: [
      { name: '周大伟', position: '副总裁', phone: '132****3355', role: 'influence', note: '推动者，但决策权在董事长' },
    ],
    currentVendors: ['用友ERP', '金蝶'], switchIntent: 'none',
    competitorNotes: '最终选择了竞品，原因是价格和本地化服务',
    behaviors: [
      { date: '2026-02-28', action: '最后联系', detail: '告知已选择其他供应商，婉拒合作', channel: 'wechat' },
    ],
    materialOpenCount: 1, websiteVisits: 3, eventAttended: [],
    creditRating: 'B', paymentHabit: 'normal', churnRisk: 'high',
    churnReason: '价格偏高，本地化服务不足，最终选择竞品',
    internalNote: '流失原因已记录，6个月后可尝试重新激活，关注竞品续约时间节点',
  },
  {
    id: 8, name: '吴佳妮', company: '南京赤诚文化传播', position: '总经理',
    phone: '131****6677', email: 'wu@chicheng.com',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20general%20manager%20headshot%2C%20business%20dress%2C%20warm%20confident%20smile%2C%20white%20background%2C%20corporate%20photography&width=100&height=100&seq=crm-c8&orientation=squarish',
    status: 'follow', source: '朋友圈营销', tags: ['文化传媒', '意向强'], lastContact: '2026-03-18', totalValue: 88000, assignee: '王芳',
    creditCode: '91320100MA1NXXXX4R', registeredCapital: '300万元', foundedYear: 2020, industry: '文化传播/内容创作',
    companySize: '20-50人', businessScope: '品牌内容策划、活动执行、新媒体运营、IP孵化',
    companyAddress: '南京市鼓楼区中山北路',
    annualBudget: '10-20万', purchaseCycle: '年度', decisionPeriod: '1-3周',
    painPoints: ['内容创作人力成本高', '活动策划效率低', '客户沟通响应慢'],
    contacts: [
      { name: '吴佳妮', position: '总经理', phone: '131****6677', role: 'decision', wechat: 'wu_jn_boss', note: '一把手，决策快，关注实用性' },
      { name: '刘运营', position: '运营主管', phone: '130****8899', role: 'user', note: '日常使用，反馈积极' },
    ],
    currentVendors: ['传统人工', '简单工具'], switchIntent: 'high',
    competitorNotes: '无强竞品，主要是从人工转向AI，决策阻力小',
    behaviors: [
      { date: '2026-03-18', action: '电话沟通', detail: '确认试用满意，询问正式采购流程', channel: 'wechat' },
      { date: '2026-03-14', action: '查看物料', detail: '打开《AI员工使用案例集》，停留20分钟', channel: 'material' },
      { date: '2026-03-10', action: '访问官网', detail: '查看定价页面，填写了试用申请表单', channel: 'website' },
    ],
    materialOpenCount: 6, websiteVisits: 12, eventAttended: ['南京创业者沙龙'],
    creditRating: 'B', paymentHabit: 'good', churnRisk: 'low',
    internalNote: '高意向客户，建议本周内发送合同，趁热打铁',
  },
];

export const followRecords: FollowRecord[] = [
  { id: 1, customerId: 1, customerName: '陈志远', type: 'meeting', content: '面谈Q2采购计划，对方对AI员工方案很感兴趣，重点关注销售自动化板块', result: '意向明确，需出正式报价单', nextAction: '发送定制化方案及报价，3天内回复', createdAt: '2026-03-18 14:30', assignee: '李明' },
  { id: 2, customerId: 3, customerName: '刘建国', type: 'call', content: '电话确认合同条款，双方对服务范围达成一致', result: '合同进入法务审核阶段', nextAction: '跟进法务审核进度，预计5个工作日', createdAt: '2026-03-17 10:15', assignee: '李明' },
  { id: 3, customerId: 2, customerName: '张美琪', type: 'wechat', content: '发送了产品演示录屏和功能说明文档，对方表示会内部讨论', result: '等待对方内部决策', nextAction: '3天后电话跟进确认进展', createdAt: '2026-03-16 16:00', assignee: '王芳' },
  { id: 4, customerId: 6, customerName: '林雪梅', type: 'visit', content: '上门拜访，展示最新物料库分享追踪功能，现场演示效果很好', result: '确认加购物料库高级版', nextAction: '发送升级报价单并安排实施', createdAt: '2026-03-19 10:00', assignee: '赵磊' },
  { id: 5, customerId: 5, customerName: '孙鹏飞', type: 'email', content: '发送竞品对比分析报告，强调我们在AI能力方面的优势', result: '对方反馈正在评估，暂无时间表', nextAction: '1周后电话跟进', createdAt: '2026-03-15 09:00', assignee: '王芳' },
  { id: 6, customerId: 8, customerName: '吴佳妮', type: 'call', content: '确认试用情况，使用满意，考虑正式采购', result: '高度意向，等报价审批', nextAction: '发送采购合同模板', createdAt: '2026-03-18 11:30', assignee: '王芳' },
];

export const opportunities: Opportunity[] = [
  { id: 1, title: '华锐科技年度AI员工采购', customerName: '陈志远', value: 280000, probability: 80, stage: 'negotiation', expectedClose: '2026-03-31', assignee: '李明', tags: ['高价值', 'Q1目标'] },
  { id: 2, title: '云合信息战略合作协议', customerName: '刘建国', value: 520000, probability: 90, stage: 'proposal', expectedClose: '2026-04-15', assignee: '李明', tags: ['战略合作'] },
  { id: 3, title: '创锐广告物料库采购', customerName: '张美琪', value: 65000, probability: 50, stage: 'demo', expectedClose: '2026-04-30', assignee: '王芳', tags: ['评估中'] },
  { id: 4, title: '赤诚文化正式采购签约', customerName: '吴佳妮', value: 88000, probability: 70, stage: 'contact', expectedClose: '2026-04-10', assignee: '王芳', tags: ['意向强'] },
  { id: 5, title: '织梦服饰物料库升级', customerName: '林雪梅', value: 45000, probability: 85, stage: 'closed_won', expectedClose: '2026-03-20', assignee: '赵磊', tags: ['已成交'] },
  { id: 6, title: '锐捷科技销售团队采购', customerName: '孙鹏飞', value: 42000, probability: 30, stage: 'lead', expectedClose: '2026-05-31', assignee: '王芳', tags: ['评估中'] },
  { id: 7, title: '智链供应链SaaS年费', customerName: '周大伟', value: 0, probability: 0, stage: 'closed_lost', expectedClose: '2026-02-28', assignee: '李明', tags: ['未成交'] },
];

export const salesLeads: SalesLead[] = [
  { id: 1, companyName: '福州鑫盛贸易', contactName: '黄总', phone: '188****1234', source: '百度搜索', score: 85, status: 'qualified', industry: '贸易', createdAt: '2026-03-19', assignee: '李明' },
  { id: 2, companyName: '长沙橙光传媒', contactName: '罗经理', phone: '177****5678', source: '朋友圈广告', score: 72, status: 'contacted', industry: '传媒', createdAt: '2026-03-18', assignee: '王芳' },
  { id: 3, companyName: '苏州量子云计算', contactName: '沈总', phone: '158****9012', source: '展会名片', score: 91, status: 'qualified', industry: '科技', createdAt: '2026-03-17', assignee: '赵磊' },
  { id: 4, companyName: '重庆金桥建设', contactName: '张部长', phone: '139****3456', source: '电话开发', score: 44, status: 'unqualified', industry: '建筑', createdAt: '2026-03-16', assignee: '李明' },
  { id: 5, companyName: '厦门蔚蓝跨境电商', contactName: '林总', phone: '166****7890', source: '老客户推荐', score: 88, status: 'new', industry: '电商', createdAt: '2026-03-19', assignee: '王芳' },
  { id: 6, companyName: '沈阳泰合汽配', contactName: '王经理', phone: '135****2345', source: '网络推广', score: 56, status: 'contacted', industry: '汽配', createdAt: '2026-03-15', assignee: '赵磊' },
  { id: 7, companyName: '宁波港通供应链', contactName: '陈董', phone: '152****6789', source: '转介绍', score: 95, status: 'qualified', industry: '供应链', createdAt: '2026-03-18', assignee: '李明' },
  { id: 8, companyName: '郑州华博餐饮集团', contactName: '马总', phone: '181****0123', source: '抖音广告', score: 38, status: 'new', industry: '餐饮', createdAt: '2026-03-19', assignee: '王芳' },
];

// ─── Extended Data ────────────────────────────────────────────────────────────
export const contracts: Contract[] = [
  { id: 1, contractNo: 'HT-2026-001', title: '华锐科技AI员工年服务协议', customerId: 1, customerName: '陈志远', amount: 280000, status: 'active', signDate: '2026-01-15', startDate: '2026-02-01', endDate: '2026-12-31', assignee: '李明', notes: '年度服务，含AI员工10个席位' },
  { id: 2, contractNo: 'HT-2026-002', title: '云合信息战略合作协议', customerId: 3, customerName: '刘建国', amount: 520000, status: 'pending', signDate: '', startDate: '2026-04-01', endDate: '2027-03-31', assignee: '李明', notes: '法务审核中，预计下周签署' },
  { id: 3, contractNo: 'HT-2026-003', title: '美橙电商物料库年费协议', customerId: 4, customerName: '王晓燕', amount: 98000, status: 'active', signDate: '2026-02-20', startDate: '2026-03-01', endDate: '2026-04-10', assignee: '赵磊', notes: '6个月服务期，即将到期' },
  { id: 4, contractNo: 'HT-2025-042', title: '织梦服饰年度框架协议', customerId: 6, customerName: '林雪梅', amount: 130000, status: 'expired', signDate: '2025-07-01', startDate: '2025-07-15', endDate: '2026-03-31', assignee: '赵磊', notes: '已到期，续约谈判中' },
  { id: 5, contractNo: 'HT-2026-004', title: '赤诚文化采购服务协议', customerId: 8, customerName: '吴佳妮', amount: 88000, status: 'active', signDate: '2026-01-10', startDate: '2026-01-15', endDate: '2026-04-20', assignee: '王芳', notes: '即将到期，需跟进续签' },
  { id: 6, contractNo: 'HT-2026-005', title: '宁波港通SaaS年费', customerId: 7, customerName: '周大伟', amount: 156000, status: 'active', signDate: '2026-01-01', startDate: '2026-01-10', endDate: '2026-04-06', assignee: '李明', notes: '2天后到期，紧急续签' },
];

export const crmPayments: CrmPayment[] = [
  { id: 1, contractNo: 'HT-2026-001', customerName: '陈志远', amount: 140000, dueDate: '2026-02-01', paidDate: '2026-01-28', status: 'paid', invoiceStatus: 'issued' },
  { id: 2, contractNo: 'HT-2026-001', customerName: '陈志远', amount: 140000, dueDate: '2026-08-01', paidDate: null, status: 'pending', invoiceStatus: 'none' },
  { id: 3, contractNo: 'HT-2026-003', customerName: '王晓燕', amount: 98000, dueDate: '2026-03-01', paidDate: '2026-03-03', status: 'paid', invoiceStatus: 'issued' },
  { id: 4, contractNo: 'HT-2025-042', customerName: '林雪梅', amount: 130000, dueDate: '2026-02-28', paidDate: null, status: 'overdue', invoiceStatus: 'applied' },
  { id: 5, contractNo: 'HT-2026-004', customerName: '吴佳妮', amount: 88000, dueDate: '2026-04-01', paidDate: null, status: 'pending', invoiceStatus: 'none' },
];

export const defaultProductCategories: ProductCategory[] = [
  { id: 'cat-1', name: '软件服务', icon: 'ri-robot-line', color: '#6366f1', bg: '#ede9fe' },
  { id: 'cat-2', name: '物料管理', icon: 'ri-folder-image-line', color: '#0ea5e9', bg: '#e0f2fe' },
  { id: 'cat-3', name: '增值服务', icon: 'ri-service-line', color: '#10b981', bg: '#dcfce7' },
  { id: 'cat-4', name: '定制服务', icon: 'ri-magic-line', color: '#f59e0b', bg: '#fef3c7' },
];

export const crmProducts: CrmProduct[] = [
  {
    id: 1, name: 'AI员工基础版', category: '软件服务', price: 9999, unit: '年/个', stock: 48, stockAlert: 10,
    description: '1个AI数字员工，基础销售&客服功能，7×12小时在线',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=modern%20AI%20digital%20employee%20software%20product%2C%20clean%20minimal%20interface%20screenshot%2C%20purple%20gradient%20background%2C%20professional%20SaaS%20product%20visualization%2C%20tech%20startup%20style%2C%20high%20quality%20render&width=400&height=240&seq=prod-1&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=modern%20AI%20digital%20employee%20software%20product%2C%20clean%20minimal%20interface%20screenshot%2C%20purple%20gradient%20background%2C%20professional%20SaaS%20product%20visualization%2C%20tech%20startup%20style%2C%20high%20quality%20render&width=400&height=240&seq=prod-1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AI%20chatbot%20mobile%20interface%2C%20conversational%20UI%20design%2C%20soft%20purple%20background%2C%20smartphone%20screen%20mockup%2C%20clean%20minimal%20product%20photo&width=400&height=240&seq=prod-1b&orientation=landscape',
      'https://readdy.ai/api/search-image?query=sales%20automation%20analytics%20dashboard%2C%20bar%20charts%20and%20KPI%20metrics%2C%20indigo%20color%20scheme%2C%20SaaS%20software%20interface%2C%20clean%20data%20visualization&width=400&height=240&seq=prod-1c&orientation=landscape',
    ],
    sku: 'SW-AI-001',
    tags: ['热销', '入门推荐'],
    highlights: ['基础销售自动化', '7×12在线服务', '标准API接入'],
  },
  {
    id: 2, name: 'AI员工专业版', category: '软件服务', price: 19999, unit: '年/个', stock: 32, stockAlert: 8,
    description: '1个AI数字员工，全功能模块，24×7在线，专属形象定制',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=professional%20AI%20assistant%20software%20dashboard%2C%20sleek%20dark%20interface%2C%20purple%20and%20indigo%20color%20scheme%2C%20enterprise%20SaaS%20product%2C%20modern%20UI%20design%2C%20high%20quality&width=400&height=240&seq=prod-2&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=professional%20AI%20assistant%20software%20dashboard%2C%20sleek%20dark%20interface%2C%20purple%20and%20indigo%20color%20scheme%2C%20enterprise%20SaaS%20product%2C%20modern%20UI%20design%2C%20high%20quality&width=400&height=240&seq=prod-2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=AI%20digital%20human%20avatar%20customization%20studio%2C%203D%20character%20design%2C%20dark%20background%20with%20purple%20glow%2C%20professional%20software%20interface&width=400&height=240&seq=prod-2b&orientation=landscape',
      'https://readdy.ai/api/search-image?query=enterprise%20CRM%20pipeline%20dashboard%2C%20dark%20theme%2C%20purple%20accent%2C%20sales%20funnel%20visualization%2C%20modern%20business%20software%20UI&width=400&height=240&seq=prod-2c&orientation=landscape',
    ],
    sku: 'SW-AI-002',
    tags: ['最受欢迎'],
    highlights: ['全功能模块', '24×7在线', '专属形象定制', '高级API'],
  },
  {
    id: 3, name: 'AI员工旗舰版', category: '软件服务', price: 39999, unit: '年/个', stock: 7, stockAlert: 10,
    description: '旗舰功能+企业级集成，专属客户成功经理支持',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=enterprise%20AI%20platform%20flagship%20product%2C%20premium%20dark%20interface%2C%20gold%20and%20purple%20accents%2C%20corporate%20software%20visualization%2C%20luxury%20tech%20product%2C%20high%20quality%20render&width=400&height=240&seq=prod-3&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=enterprise%20AI%20platform%20flagship%20product%2C%20premium%20dark%20interface%2C%20gold%20and%20purple%20accents%2C%20corporate%20software%20visualization%2C%20luxury%20tech%20product%2C%20high%20quality%20render&width=400&height=240&seq=prod-3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=private%20cloud%20server%20room%2C%20enterprise%20infrastructure%2C%20dark%20background%20with%20gold%20lighting%2C%20technology%20data%20center%20visualization&width=400&height=240&seq=prod-3b&orientation=landscape',
      'https://readdy.ai/api/search-image?query=enterprise%20software%20API%20integration%20diagram%2C%20dark%20background%2C%20gold%20and%20amber%20colors%2C%20technical%20architecture%20illustration%2C%20system%20connection%20map&width=400&height=240&seq=prod-3c&orientation=landscape',
    ],
    sku: 'SW-AI-003',
    tags: ['企业首选'],
    highlights: ['企业级集成', '专属CSM', '私有化部署', '无限席位'],
  },
  {
    id: 4, name: '物料库基础版', category: '物料管理', price: 4999, unit: '年', stock: 120, stockAlert: 20,
    description: '素材存储1TB，智能分类，外链分享追踪100次/月',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=digital%20asset%20management%20software%2C%20clean%20blue%20interface%2C%20file%20storage%20and%20organization%20UI%2C%20modern%20SaaS%20product%20screenshot%2C%20sky%20blue%20background%2C%20professional%20design&width=400&height=240&seq=prod-4&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=digital%20asset%20management%20software%2C%20clean%20blue%20interface%2C%20file%20storage%20and%20organization%20UI%2C%20modern%20SaaS%20product%20screenshot%2C%20sky%20blue%20background%2C%20professional%20design&width=400&height=240&seq=prod-4&orientation=landscape',
      'https://readdy.ai/api/search-image?query=file%20sharing%20link%20tracking%20analytics%2C%20blue%20theme%2C%20click%20statistics%20dashboard%2C%20modern%20web%20app%20interface%2C%20clean%20minimal%20design&width=400&height=240&seq=prod-4b&orientation=landscape',
    ],
    sku: 'MAT-001',
    tags: ['入门版'],
    highlights: ['1TB存储', '智能分类', '分享追踪100次/月'],
  },
  {
    id: 5, name: '物料库高级版', category: '物料管理', price: 9999, unit: '年', stock: 15, stockAlert: 20,
    description: '素材存储10TB，无限外链，API接口，团队协作无限席位',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=advanced%20digital%20asset%20management%20platform%2C%20team%20collaboration%20interface%2C%20blue%20and%20teal%20color%20scheme%2C%20enterprise%20content%20management%2C%20modern%20UI%2C%20high%20quality&width=400&height=240&seq=prod-5&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=advanced%20digital%20asset%20management%20platform%2C%20team%20collaboration%20interface%2C%20blue%20and%20teal%20color%20scheme%2C%20enterprise%20content%20management%2C%20modern%20UI%2C%20high%20quality&width=400&height=240&seq=prod-5&orientation=landscape',
      'https://readdy.ai/api/search-image?query=team%20collaboration%20workspace%2C%20multiple%20users%20editing%20documents%2C%20teal%20blue%20interface%2C%20real-time%20sync%20visualization%2C%20modern%20SaaS%20product&width=400&height=240&seq=prod-5b&orientation=landscape',
      'https://readdy.ai/api/search-image?query=API%20developer%20portal%20documentation%2C%20blue%20color%20scheme%2C%20code%20snippets%20on%20screen%2C%20technical%20software%20product%20interface&width=400&height=240&seq=prod-5c&orientation=landscape',
    ],
    sku: 'MAT-002',
    tags: ['团队推荐'],
    highlights: ['10TB存储', '无限外链', 'API接口', '无限席位'],
  },
  {
    id: 6, name: '企业实施服务', category: '增值服务', price: 15000, unit: '次', stock: null, stockAlert: null,
    description: '驻场3天，需求调研、系统配置、员工培训',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=enterprise%20implementation%20consulting%20service%2C%20professional%20team%20meeting%2C%20green%20and%20emerald%20color%20scheme%2C%20business%20consulting%20visualization%2C%20clean%20minimal%20background&width=400&height=240&seq=prod-6&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=enterprise%20implementation%20consulting%20service%2C%20professional%20team%20meeting%2C%20green%20and%20emerald%20color%20scheme%2C%20business%20consulting%20visualization%2C%20clean%20minimal%20background&width=400&height=240&seq=prod-6&orientation=landscape',
      'https://readdy.ai/api/search-image?query=employee%20training%20workshop%2C%20instructor%20teaching%20software%20system%2C%20emerald%20green%20background%2C%20corporate%20training%20session%2C%20modern%20office%20environment&width=400&height=240&seq=prod-6b&orientation=landscape',
    ],
    sku: 'SVC-001',
    tags: ['专业服务'],
    highlights: ['驻场3天', '需求调研', '系统配置', '员工培训'],
  },
  {
    id: 7, name: '年度客户成功服务', category: '增值服务', price: 30000, unit: '年', stock: null, stockAlert: null,
    description: '专属客户成功经理，季度商务复盘，优先技术支持',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=customer%20success%20management%20service%2C%20professional%20support%20team%2C%20emerald%20green%20background%2C%20business%20partnership%20visualization%2C%20modern%20clean%20design&width=400&height=240&seq=prod-7&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=customer%20success%20management%20service%2C%20professional%20support%20team%2C%20emerald%20green%20background%2C%20business%20partnership%20visualization%2C%20modern%20clean%20design&width=400&height=240&seq=prod-7&orientation=landscape',
      'https://readdy.ai/api/search-image?query=quarterly%20business%20review%20meeting%2C%20data%20presentation%20on%20screen%2C%20green%20theme%2C%20professional%20executives%20in%20conference%20room&width=400&height=240&seq=prod-7b&orientation=landscape',
    ],
    sku: 'SVC-002',
    tags: ['年度服务'],
    highlights: ['专属CSM', '季度复盘', '优先技术支持'],
  },
  {
    id: 8, name: '定制AI员工形象', category: '定制服务', price: 8000, unit: '次', stock: null, stockAlert: null,
    description: '3D建模、动态表情、品牌服装定制，交付15个工作日',
    status: 'active',
    image: 'https://readdy.ai/api/search-image?query=custom%20AI%20avatar%20design%20service%2C%203D%20character%20modeling%2C%20amber%20and%20gold%20color%20scheme%2C%20digital%20human%20creation%2C%20creative%20design%20studio%20visualization&width=400&height=240&seq=prod-8&orientation=landscape',
    images: [
      'https://readdy.ai/api/search-image?query=custom%20AI%20avatar%20design%20service%2C%203D%20character%20modeling%2C%20amber%20and%20gold%20color%20scheme%2C%20digital%20human%20creation%2C%20creative%20design%20studio%20visualization&width=400&height=240&seq=prod-8&orientation=landscape',
      'https://readdy.ai/api/search-image?query=3D%20character%20facial%20expression%20animation%2C%20digital%20human%20face%20rigging%2C%20warm%20amber%20lighting%2C%20creative%20studio%20render%2C%20high%20quality&width=400&height=240&seq=prod-8b&orientation=landscape',
      'https://readdy.ai/api/search-image?query=brand%20costume%20design%20for%20virtual%20character%2C%20fashion%20design%20illustration%2C%20warm%20amber%20background%2C%20creative%20design%20process%2C%20professional%20artwork&width=400&height=240&seq=prod-8c&orientation=landscape',
    ],
    sku: 'CUS-001',
    tags: ['定制专属'],
    highlights: ['3D建模', '动态表情', '品牌服装', '15工作日交付'],
  },
];

export const followTasks: FollowTask[] = [
  { id: 1, customerId: 1, customerName: '陈志远', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20business%20executive%20portrait%20headshot%2C%20formal%20attire%2C%20confident%20smile%2C%20clean%20white%20background&width=60&height=60&seq=crm-ft1&orientation=squarish', taskType: 'call', plannedAt: '2026-04-01 09:30', status: 'pending', priority: 'high', note: '跟进合同终版确认，需确认付款方式及开票信息', assignee: '李明' },
  { id: 2, customerId: 8, customerName: '吴佳妮', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20general%20manager%20headshot%2C%20business%20dress%2C%20warm%20smile&width=60&height=60&seq=crm-ft3&orientation=squarish', taskType: 'wechat', plannedAt: '2026-04-01 11:00', status: 'pending', priority: 'high', note: '发送合同草稿，确认签约时间安排', assignee: '王芳' },
  { id: 3, customerId: 2, customerName: '张美琪', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20business%20executive%20portrait%20headshot%2C%20formal%20attire%2C%20warm%20smile%2C%20clean%20white%20background&width=60&height=60&seq=crm-ft2&orientation=squarish', taskType: 'email', plannedAt: '2026-04-01 14:00', status: 'pending', priority: 'medium', note: '回复竞品对比问题，发送升级版功能说明', assignee: '王芳' },
  { id: 4, customerId: 6, customerName: '林雪梅', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20brand%20director%20headshot%2C%20stylish%20business%20attire&width=60&height=60&seq=crm-ft5&orientation=squarish', taskType: 'call', plannedAt: '2026-04-01 16:30', status: 'pending', priority: 'medium', note: '物料库高级版升级进度确认，安排实施日期', assignee: '赵磊' },
  { id: 5, customerId: 5, customerName: '孙鹏飞', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20sales%20director%20headshot%2C%20suit%2C%20confident%20posture&width=60&height=60&seq=crm-ft4&orientation=squarish', taskType: 'call', plannedAt: '2026-04-02 10:00', status: 'pending', priority: 'low', note: '跟进评估进展，询问内部决策时间节点', assignee: '王芳' },
  { id: 6, customerId: 3, customerName: '刘建国', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20CEO%20portrait%20headshot%2C%20business%20casual%2C%20confident%20look&width=60&height=60&seq=crm-ft6&orientation=squarish', taskType: 'meeting', plannedAt: '2026-04-02 14:30', status: 'pending', priority: 'high', note: '战略合作签约仪式，需提前准备合同副本及礼品', assignee: '李明' },
  { id: 7, customerId: 4, customerName: '王晓燕', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20female%20operations%20director%20headshot%2C%20business%20attire%2C%20friendly%20expression%2C%20white%20background&width=60&height=60&seq=crm-ft7&orientation=squarish', taskType: 'visit', plannedAt: '2026-04-03 10:00', status: 'pending', priority: 'medium', note: '上门拜访，了解续约意向，展示新功能', assignee: '赵磊' },
  { id: 8, customerId: 1, customerName: '陈志远', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20business%20executive%20portrait%20headshot%2C%20formal%20attire%2C%20confident%20smile%2C%20clean%20white%20background&width=60&height=60&seq=crm-ft1&orientation=squarish', taskType: 'meeting', plannedAt: '2026-04-04 09:00', status: 'pending', priority: 'high', note: '季度业务复盘会议，全面回顾使用效果', assignee: '李明' },
  { id: 9, customerId: 7, customerName: '周大伟', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20VP%20portrait%20headshot%2C%20formal%20business%20suit%2C%20authoritative%20look&width=60&height=60&seq=crm-ft9&orientation=squarish', taskType: 'call', plannedAt: '2026-03-31 15:00', status: 'overdue', priority: 'medium', note: '流失原因复盘，了解改进方向（已超时）', assignee: '李明' },
  { id: 10, customerId: 5, customerName: '孙鹏飞', customerAvatar: 'https://readdy.ai/api/search-image?query=professional%20chinese%20male%20sales%20director%20headshot%2C%20suit%2C%20confident%20posture&width=60&height=60&seq=crm-ft4&orientation=squarish', taskType: 'wechat', plannedAt: '2026-04-05 10:00', status: 'pending', priority: 'low', note: '发送行业解决方案白皮书', assignee: '王芳' },
];

export const crmActivities: CrmActivity[] = [
  { id: 1, type: 'deal', content: '刘建国（云合信息）商机进入合同谈判阶段，预计签约 ¥52万', time: '10分钟前', user: '李明' },
  { id: 2, type: 'new_lead', content: '新线索「宁波港通供应链」已录入，质量评分 95分', time: '35分钟前', user: '系统自动' },
  { id: 3, type: 'follow', content: '林雪梅（织梦服饰）跟进完成：确认加购物料库高级版', time: '1小时前', user: '赵磊' },
  { id: 4, type: 'payment', content: '王晓燕（美橙电商）款项 ¥98,000 已到账', time: '2小时前', user: '系统' },
  { id: 5, type: 'contract', content: '合同 HT-2026-001 已激活，服务开始计时', time: '昨天 16:30', user: '系统' },
  { id: 6, type: 'lost', content: '周大伟（智链供应链）已移入流失客户池', time: '昨天 14:00', user: '李明' },
  { id: 7, type: 'new_lead', content: '新线索「厦门蔚蓝跨境电商」已分配给王芳', time: '昨天 10:20', user: '系统自动' },
];

export const crmTags: CrmTag[] = [
  { id: 1,  name: '高价值',   color: '#ef4444', category: 'system', count: 3 },
  { id: 2,  name: '重点跟进', color: '#f59e0b', category: 'system', count: 5 },
  { id: 3,  name: '已成交',   color: '#10b981', category: 'system', count: 4 },
  { id: 4,  name: '流失风险', color: '#6366f1', category: 'system', count: 2 },
  { id: 5,  name: 'KA客户',   color: '#0ea5e9', category: 'system', count: 2 },
  { id: 6,  name: '科技行业', color: '#8b5cf6', category: 'custom', count: 4 },
  { id: 7,  name: '广告传媒', color: '#f97316', category: 'custom', count: 3 },
  { id: 8,  name: '电商',     color: '#14b8a6', category: 'custom', count: 3 },
  { id: 9,  name: '续约中',   color: '#ec4899', category: 'custom', count: 2 },
  { id: 10, name: '新客户',   color: '#84cc16', category: 'custom', count: 4 },
];

// ─── 分享追踪数据 ─────────────────────────────────────────────────────────────
export const materialShareRecords: Record<number, MaterialShareRecord[]> = {
  // 陈志远（客户ID=1）
  1: [
    {
      id: 101, materialName: 'AI员工ROI分析报告', materialType: 'PDF报告',
      shareTime: '2026-03-15 10:22', sharedBy: '李明',
      shareLink: 'share.xxx.com/s/a1b2c3', totalOpens: 6, totalForwards: 2, uniqueUsers: 4, avgDuration: 438,
      openRecords: [
        { id: 1001, openTime: '2026-03-15 11:05', user: '陈志远', device: 'iPhone 15 Pro', os: 'iOS 17', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 720, pageViews: 12, hotPage: '第4页：ROI测算模型', hotPageDuration: 245, isForward: false },
        { id: 1002, openTime: '2026-03-15 14:30', user: '李副总', device: 'MacBook Pro', os: 'macOS 14', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 540, pageViews: 8, hotPage: '第6页：竞品对比', hotPageDuration: 180, isForward: true, forwardFrom: '陈志远' },
        { id: 1003, openTime: '2026-03-16 09:15', user: '赵采购', device: 'HUAWEI Mate 60', os: 'HarmonyOS 4', location: '深圳市福田区', ip: '183.14.xxx.xxx', duration: 320, pageViews: 5, hotPage: '第8页：价格方案', hotPageDuration: 156, isForward: true, forwardFrom: '陈志远' },
        { id: 1004, openTime: '2026-03-16 15:48', user: '陈志远', device: 'iPad Pro', os: 'iPadOS 17', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 890, pageViews: 15, hotPage: '第4页：ROI测算模型', hotPageDuration: 312, isForward: false },
        { id: 1005, openTime: '2026-03-17 10:00', user: '未知访客', device: 'Windows PC', os: 'Windows 11', location: '深圳市龙华区', ip: '58.61.xxx.xxx', duration: 120, pageViews: 3, hotPage: '第1页：封面', hotPageDuration: 60, isForward: true, forwardFrom: '李副总' },
        { id: 1006, openTime: '2026-03-18 08:30', user: '陈志远', device: 'iPhone 15 Pro', os: 'iOS 17', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 280, pageViews: 6, hotPage: '第4页：ROI测算模型', hotPageDuration: 98, isForward: false },
      ],
    },
    {
      id: 102, materialName: '企业AI员工部署方案', materialType: 'PPT演示',
      shareTime: '2026-03-10 16:00', sharedBy: '李明',
      shareLink: 'share.xxx.com/s/d4e5f6', totalOpens: 4, totalForwards: 1, uniqueUsers: 3, avgDuration: 612,
      openRecords: [
        { id: 1007, openTime: '2026-03-10 17:20', user: '陈志远', device: 'iPhone 15 Pro', os: 'iOS 17', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 480, pageViews: 10, hotPage: '第3页：技术架构', hotPageDuration: 200, isForward: false },
        { id: 1008, openTime: '2026-03-11 09:00', user: '张CTO', device: 'MacBook Air', os: 'macOS 14', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 960, pageViews: 18, hotPage: '第5页：API集成说明', hotPageDuration: 380, isForward: true, forwardFrom: '陈志远' },
        { id: 1009, openTime: '2026-03-11 14:00', user: '陈志远', device: 'MacBook Pro', os: 'macOS 14', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 720, pageViews: 14, hotPage: '第3页：技术架构', hotPageDuration: 260, isForward: false },
        { id: 1010, openTime: '2026-03-12 10:30', user: '李副总', device: 'HUAWEI P60', os: 'HarmonyOS 4', location: '深圳市南山区', ip: '120.229.xxx.xxx', duration: 290, pageViews: 6, hotPage: '第7页：实施周期', hotPageDuration: 120, isForward: false },
      ],
    },
  ],
  // 张美琪（客户ID=2）
  2: [
    {
      id: 201, materialName: '物料库功能演示录屏', materialType: '视频',
      shareTime: '2026-03-16 09:30', sharedBy: '王芳',
      shareLink: 'share.xxx.com/s/g7h8i9', totalOpens: 5, totalForwards: 3, uniqueUsers: 4, avgDuration: 285,
      openRecords: [
        { id: 2001, openTime: '2026-03-16 10:15', user: '张美琪', device: 'iPhone 14', os: 'iOS 16', location: '上海市静安区', ip: '101.86.xxx.xxx', duration: 420, pageViews: 1, hotPage: '视频全程', hotPageDuration: 420, isForward: false },
        { id: 2002, openTime: '2026-03-16 11:30', user: '王总', device: 'iPad Air', os: 'iPadOS 16', location: '上海市静安区', ip: '101.86.xxx.xxx', duration: 180, pageViews: 1, hotPage: '视频前半段', hotPageDuration: 180, isForward: true, forwardFrom: '张美琪' },
        { id: 2003, openTime: '2026-03-16 14:00', user: '设计主管', device: 'MacBook Pro', os: 'macOS 13', location: '上海市静安区', ip: '101.86.xxx.xxx', duration: 360, pageViews: 1, hotPage: '视频全程', hotPageDuration: 360, isForward: true, forwardFrom: '张美琪' },
        { id: 2004, openTime: '2026-03-17 09:00', user: '张美琪', device: 'iPhone 14', os: 'iOS 16', location: '上海市静安区', ip: '101.86.xxx.xxx', duration: 240, pageViews: 1, hotPage: '视频后半段', hotPageDuration: 240, isForward: false },
        { id: 2005, openTime: '2026-03-17 16:00', user: '未知访客', device: 'Android', os: 'Android 13', location: '上海市黄浦区', ip: '180.168.xxx.xxx', duration: 210, pageViews: 1, hotPage: '视频全程', hotPageDuration: 210, isForward: true, forwardFrom: '设计主管' },
      ],
    },
  ],
  // 刘建国（客户ID=3）
  3: [
    {
      id: 301, materialName: '企业级AI员工部署方案', materialType: 'PDF报告',
      shareTime: '2026-03-05 14:00', sharedBy: '李明',
      shareLink: 'share.xxx.com/s/j1k2l3', totalOpens: 8, totalForwards: 4, uniqueUsers: 6, avgDuration: 520,
      openRecords: [
        { id: 3001, openTime: '2026-03-05 15:30', user: '刘建国', device: 'MacBook Pro', os: 'macOS 14', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 680, pageViews: 14, hotPage: '第5页：大规模部署方案', hotPageDuration: 280, isForward: false },
        { id: 3002, openTime: '2026-03-05 17:00', user: '张CTO', device: 'MacBook Air', os: 'macOS 14', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 1200, pageViews: 22, hotPage: '第8页：API文档', hotPageDuration: 480, isForward: true, forwardFrom: '刘建国' },
        { id: 3003, openTime: '2026-03-06 09:30', user: '孙CFO', device: 'iPad Pro', os: 'iPadOS 17', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 320, pageViews: 6, hotPage: '第10页：价格体系', hotPageDuration: 180, isForward: true, forwardFrom: '刘建国' },
        { id: 3004, openTime: '2026-03-06 14:00', user: '李运营', device: 'HUAWEI Mate 60', os: 'HarmonyOS 4', location: '北京市朝阳区', ip: '123.120.xxx.xxx', duration: 450, pageViews: 9, hotPage: '第6页：操作界面演示', hotPageDuration: 200, isForward: true, forwardFrom: '刘建国' },
        { id: 3005, openTime: '2026-03-07 10:00', user: '刘建国', device: 'iPhone 15', os: 'iOS 17', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 560, pageViews: 11, hotPage: '第5页：大规模部署方案', hotPageDuration: 220, isForward: false },
        { id: 3006, openTime: '2026-03-07 15:30', user: '技术工程师', device: 'Windows PC', os: 'Windows 11', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 780, pageViews: 16, hotPage: '第8页：API文档', hotPageDuration: 350, isForward: true, forwardFrom: '张CTO' },
        { id: 3007, openTime: '2026-03-08 09:00', user: '张CTO', device: 'MacBook Air', os: 'macOS 14', location: '北京市海淀区', ip: '114.242.xxx.xxx', duration: 420, pageViews: 8, hotPage: '第9页：安全合规', hotPageDuration: 190, isForward: false },
        { id: 3008, openTime: '2026-03-09 11:00', user: '未知访客', device: 'Android', os: 'Android 14', location: '北京市西城区', ip: '36.110.xxx.xxx', duration: 180, pageViews: 4, hotPage: '第1页：封面', hotPageDuration: 80, isForward: true, forwardFrom: '技术工程师' },
      ],
    },
  ],
};

// ─── 每周互动热力图数据 ────────────────────────────────────────────────────────
export const customerWeeklyHeatmap: Record<number, WeeklyHeatmapData[]> = {
  // 陈志远（高活跃）
  1: [
    { week: 'W1', weekLabel: '1/6', days: [0, 1, 0, 2, 1, 0, 0], total: 4 },
    { week: 'W2', weekLabel: '1/13', days: [1, 0, 2, 1, 0, 1, 0], total: 5 },
    { week: 'W3', weekLabel: '1/20', days: [0, 2, 1, 3, 2, 0, 0], total: 8 },
    { week: 'W4', weekLabel: '1/27', days: [1, 1, 0, 2, 1, 0, 0], total: 5 },
    { week: 'W5', weekLabel: '2/3', days: [2, 0, 1, 2, 3, 0, 0], total: 8 },
    { week: 'W6', weekLabel: '2/10', days: [0, 1, 2, 1, 0, 0, 0], total: 4 },
    { week: 'W7', weekLabel: '2/17', days: [1, 2, 1, 3, 2, 0, 0], total: 9 },
    { week: 'W8', weekLabel: '2/24', days: [2, 1, 3, 2, 1, 0, 0], total: 9 },
    { week: 'W9', weekLabel: '3/3', days: [1, 3, 2, 4, 3, 0, 0], total: 13 },
    { week: 'W10', weekLabel: '3/10', days: [3, 2, 4, 3, 2, 0, 0], total: 14 },
    { week: 'W11', weekLabel: '3/17', days: [2, 4, 3, 4, 3, 0, 0], total: 16 },
    { week: 'W12', weekLabel: '3/24', days: [3, 2, 4, 3, 4, 0, 0], total: 16 },
  ],
  // 张美琪（中等活跃）
  2: [
    { week: 'W1', weekLabel: '1/6', days: [0, 0, 1, 0, 0, 0, 0], total: 1 },
    { week: 'W2', weekLabel: '1/13', days: [0, 1, 0, 0, 1, 0, 0], total: 2 },
    { week: 'W3', weekLabel: '1/20', days: [1, 0, 0, 1, 0, 0, 0], total: 2 },
    { week: 'W4', weekLabel: '1/27', days: [0, 0, 1, 0, 1, 0, 0], total: 2 },
    { week: 'W5', weekLabel: '2/3', days: [0, 1, 0, 2, 0, 0, 0], total: 3 },
    { week: 'W6', weekLabel: '2/10', days: [1, 0, 1, 0, 0, 0, 0], total: 2 },
    { week: 'W7', weekLabel: '2/17', days: [0, 2, 1, 0, 1, 0, 0], total: 4 },
    { week: 'W8', weekLabel: '2/24', days: [1, 0, 2, 1, 0, 0, 0], total: 4 },
    { week: 'W9', weekLabel: '3/3', days: [0, 1, 0, 2, 1, 0, 0], total: 4 },
    { week: 'W10', weekLabel: '3/10', days: [2, 1, 0, 2, 1, 0, 0], total: 6 },
    { week: 'W11', weekLabel: '3/17', days: [1, 3, 2, 1, 0, 0, 0], total: 7 },
    { week: 'W12', weekLabel: '3/24', days: [2, 1, 3, 2, 1, 0, 0], total: 9 },
  ],
  // 刘建国（极高活跃）
  3: [
    { week: 'W1', weekLabel: '1/6', days: [1, 2, 1, 2, 1, 0, 0], total: 7 },
    { week: 'W2', weekLabel: '1/13', days: [2, 1, 3, 2, 1, 0, 0], total: 9 },
    { week: 'W3', weekLabel: '1/20', days: [1, 3, 2, 3, 2, 0, 0], total: 11 },
    { week: 'W4', weekLabel: '1/27', days: [2, 2, 1, 3, 2, 0, 0], total: 10 },
    { week: 'W5', weekLabel: '2/3', days: [3, 1, 2, 3, 2, 0, 0], total: 11 },
    { week: 'W6', weekLabel: '2/10', days: [1, 2, 3, 2, 1, 0, 0], total: 9 },
    { week: 'W7', weekLabel: '2/17', days: [2, 3, 2, 4, 3, 0, 0], total: 14 },
    { week: 'W8', weekLabel: '2/24', days: [3, 2, 4, 3, 2, 0, 0], total: 14 },
    { week: 'W9', weekLabel: '3/3', days: [2, 4, 3, 4, 3, 0, 0], total: 16 },

    { week: 'W10', weekLabel: '3/10', days: [4, 3, 4, 4, 3, 0, 0], total: 18 },
    { week: 'W11', weekLabel: '3/17', days: [3, 4, 4, 4, 4, 0, 0], total: 19 },
    { week: 'W12', weekLabel: '3/24', days: [4, 3, 4, 4, 4, 0, 0], total: 19 },
  ],
};

// ─── 每日互动明细数据 ──────────────────────────────────────────────────────────
export const customerDailyInteractions: DailyInteractionMap = {
  // 陈志远（客户ID=1）
  1: {
    '3/24': [
      { id: 1, time: '09:15', type: 'material', title: '打开《AI员工ROI分析报告》', detail: '停留12分钟，重点浏览第4页ROI测算模型', duration: 720 },
      { id: 2, time: '11:30', type: 'wechat', title: '微信沟通', detail: '询问AI员工部署周期和培训支持方案', duration: 480 },
      { id: 3, time: '14:22', type: 'website', title: '访问官网产品页', detail: '浏览「企业版」定价页面，停留约8分钟', duration: 480 },
      { id: 4, time: '16:05', type: 'email', title: '回复邮件', detail: '收到技术团队评估报告，整体评价正面', duration: 0 },
      { id: 5, time: '19:05', type: 'material', title: '再次打开ROI报告', detail: '晚间再次查看，停留6分钟，可能在向领导汇报', duration: 360 },
    ],
    '3/25': [
      { id: 6, time: '10:00', type: 'call', title: '电话沟通', detail: '确认决策链，总经理已知晓，等待董事会批准', duration: 900 },
      { id: 7, time: '16:00', type: 'wechat', title: '微信沟通', detail: '发来竞品对比问题，要求提供详细对比表', duration: 300 },
      { id: 8, time: '17:20', type: 'email', title: '发送签约意向函', detail: '正式发来签约意向确认邮件', duration: 0 },
      { id: 9, time: '19:05', type: 'material', title: '打开《产品介绍PPT》', detail: '浏览产品概览，停留15分钟', duration: 900 },
    ],
    '3/26': [
      { id: 10, time: '09:45', type: 'website', title: '访问官网案例页', detail: '查看同行业客户案例，停留15分钟', duration: 900 },
      { id: 11, time: '14:00', type: 'wechat', title: '微信沟通', detail: '询问是否有免费试用方案', duration: 240 },
      { id: 12, time: '16:30', type: 'email', title: '发送询价邮件', detail: '要求提供团队版报价方案', duration: 0 },
      { id: 13, time: '17:30', type: 'material', title: '打开《团队协作方案》', detail: '与技术团队共同审阅，停留15分钟', duration: 900 },
    ],
    '3/27': [
      { id: 14, time: '10:30', type: 'call', title: '电话跟进', detail: '讨论合同条款，对价格有异议，需要进一步谈判', duration: 1800 },
      { id: 15, time: '16:00', type: 'wechat', title: '微信沟通', detail: '确认修改后的合同版本，表示满意', duration: 600 },
    ],
    '3/28': [
      { id: 16, time: '09:00', type: 'material', title: '打开《合同条款说明》', detail: '仔细阅读合同条款，停留22分钟', duration: 1320 },
      { id: 17, time: '11:15', type: 'wechat', title: '微信沟通', detail: '确认签约时间安排', duration: 420 },
      { id: 18, time: '14:30', type: 'email', title: '发送签约确认函', detail: '正式确认签约时间和地点', duration: 0 },
      { id: 19, time: '16:45', type: 'call', title: '电话确认', detail: '最终确认签约时间为下周一上午10点', duration: 600 },
    ],
  },
  // 张美琪（客户ID=2）
  2: {
    '3/24': [
      { id: 101, time: '10:15', type: 'material', title: '打开《物料库功能演示》', detail: '查看视频演示，停留7分钟', duration: 420 },
      { id: 102, time: '14:00', type: 'wechat', title: '微信沟通', detail: '询问物料库的权限管理功能', duration: 300 },
      { id: 103, time: '16:30', type: 'website', title: '访问官网', detail: '浏览功能介绍页面', duration: 240 },
    ],
    '3/25': [
      { id: 104, time: '09:00', type: 'call', title: '电话沟通', detail: '讨论团队协作功能需求', duration: 900 },
      { id: 105, time: '15:00', type: 'material', title: '打开《团队协作方案》', detail: '详细阅读，停留15分钟', duration: 900 },
    ],
    '3/26': [
      { id: 106, time: '10:30', type: 'material', title: '打开《产品功能手册》', detail: '全面了解产品功能，停留20分钟', duration: 1200 },
      { id: 107, time: '14:00', type: 'wechat', title: '微信沟通', detail: '对物料追踪功能很感兴趣', duration: 480 },
      { id: 108, time: '16:30', type: 'email', title: '发送询价邮件', detail: '要求提供团队版报价方案', duration: 0 },
    ],
  },
  // 刘建国（客户ID=3）
  3: {
    '3/24': [
      { id: 201, time: '08:30', type: 'call', title: '早间电话', detail: '确认合同最终条款，双方基本达成一致', duration: 1800 },
      { id: 202, time: '10:00', type: 'material', title: '打开《企业级部署方案》', detail: '与技术团队共同审阅，停留25分钟', duration: 1500 },
      { id: 203, time: '11:30', type: 'wechat', title: '微信沟通', detail: '发来法务审核意见，需要修改两处条款', duration: 600 },
      { id: 204, time: '14:00', type: 'email', title: '发送修改版合同', detail: '收到修改后合同，表示满意', duration: 0 },
      { id: 205, time: '16:00', type: 'website', title: '访问客户案例页', detail: '查看同规模企业实施案例', duration: 480 },
    ],
    '3/25': [
      { id: 206, time: '09:00', type: 'call', title: '电话确认', detail: '最终确认签约，安排签约仪式', duration: 600 },
      { id: 207, time: '11:00', type: 'material', title: '打开《实施计划书》', detail: '了解实施步骤和时间节点', duration: 900 },
      { id: 208, time: '14:30', type: 'wechat', title: '微信沟通', detail: '确认参与签约的人员名单', duration: 300 },
      { id: 209, time: '16:00', type: 'email', title: '发送签约确认函', detail: '正式确认签约时间和地点', duration: 0 },
    ],
  },
};
